import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'

const SUPA_URL = import.meta.env.VITE_SUPABASE_URL

/**
 * MetaAdsHub — Panel de Meta Ads dentro del Marketing Hub Limpack.
 *
 * Fase 1 (14-jul-2026, deploy inicial):
 *  - 8 KPIs top: spend hoy/mes, CPC, CTR, landing views, signups atribuidos, CPA, balance
 *  - Banner de alertas + card sugerencias del agente
 *  - Tabla de campañas con expandible detalle
 *  - Botón "Aplicar" en sugerencias -> por ahora abre confirm (Fase 2 ejecuta acciones)
 *
 * Data:
 *  - edge fn meta-ads-insights (?tipo=dashboard) → account + campaigns + insights hoy/total
 *  - edge fn meta-ads-attribution → recalcula CPA/ROAS + genera sugerencias
 *  - tabla meta_ads_snapshots_daily (histórico local)
 *  - tabla meta_ads_sugerencias (pendientes del agente)
 */
export default function MetaAdsHub() {
  const [data, setData] = useState(null)  // { account, campaigns, insights, agregado_mes }
  const [sugerencias, setSugerencias] = useState([])
  const [snapshots, setSnapshots] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [campExpandidaId, setCampExpandidaId] = useState(null)
  const [ultimaSync, setUltimaSync] = useState(null)

  async function cargar({ recalcular = false } = {}) {
    setLoading(true)
    setError(null)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const token = session?.access_token
      if (!token) throw new Error('No hay sesión activa')

      // 1) Dashboard (Meta Graph API + cache)
      const resDash = await fetch(`${SUPA_URL}/functions/v1/meta-ads-insights?tipo=dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` },
      })
      const dashJson = await resDash.json()
      if (!resDash.ok || dashJson.error) throw new Error(dashJson.error || 'error al leer Meta Ads')
      setData(dashJson)
      setUltimaSync(new Date())

      // 2) Attribution: recalcula signups + genera sugerencias (siempre si recalcular=true, sino solo si no hay data)
      if (recalcular) {
        await fetch(`${SUPA_URL}/functions/v1/meta-ads-attribution`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
        })
      }

      // 3) Sugerencias pendientes
      const { data: sug } = await supabase
        .from('meta_ads_sugerencias')
        .select('*')
        .eq('estado', 'pendiente')
        .gte('generada_at', new Date(Date.now() - 48 * 3600 * 1000).toISOString())
        .order('prioridad', { ascending: false })
        .order('generada_at', { ascending: false })
        .limit(10)
      setSugerencias(sug || [])

      // 4) Snapshots últimos 30 días para KPIs mes + histórico
      const desde = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10)
      const { data: snaps } = await supabase
        .from('meta_ads_snapshots_daily')
        .select('*')
        .gte('fecha', desde)
        .order('fecha', { ascending: false })
      setSnapshots(snaps || [])
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { cargar({ recalcular: true }) }, [])

  // --------------------------------------------------------------------------
  // KPIs derivados
  // --------------------------------------------------------------------------
  const kpis = useMemo(() => {
    if (!data) return null
    const insHoy = (data.insights || [])
      .map(i => i.hoy).filter(Boolean)
    const spendHoy = insHoy.reduce((s, i) => s + (parseFloat(i.spend) || 0), 0)
    const clicksHoy = insHoy.reduce((s, i) => s + (parseInt(i.clicks) || 0), 0)
    const impresionesHoy = insHoy.reduce((s, i) => s + (parseInt(i.impressions) || 0), 0)
    const lpvHoy = insHoy.reduce((s, i) => {
      const actions = i.actions || []
      const lpv = actions.find(a => a.action_type === 'landing_page_view')?.value
      return s + (parseInt(lpv || '0') || 0)
    }, 0)
    const ctrProm = impresionesHoy > 0 ? (clicksHoy / impresionesHoy * 100) : 0
    const cpcProm = clicksHoy > 0 ? spendHoy / clicksHoy : 0
    // Meta devuelve `balance` en centavos (unidad más chica de la currency)
    // No es "saldo disponible" — es "spend unbilled" (lo que Meta te va a cobrar en la próxima factura)
    const balance = parseFloat(data.account?.balance || '0') / 100
    const amountSpentTotal = parseFloat(data.account?.amount_spent || '0') / 100
    const currency = data.account?.currency || 'USD'
    const mes = data.agregado_mes || {}
    const signupsMes = mes.signups || 0
    const spendMes = mes.spend || 0
    const cpaMes = signupsMes > 0 ? spendMes / signupsMes : null
    return {
      spendHoy, clicksHoy, impresionesHoy, lpvHoy, ctrProm, cpcProm,
      balance, amountSpentTotal, currency, mes,
      signupsMes, spendMes, cpaMes,
    }
  }, [data])

  // --------------------------------------------------------------------------
  // Alertas críticas
  // --------------------------------------------------------------------------
  const alertas = useMemo(() => {
    if (!kpis || !data) return []
    const out = []
    // "Balance" en Meta postpaid = spend unbilled. NO es "saldo disponible".
    // Solo alertar si el balance sin facturar es alto (>$50) — puede indicar problema con el pago.
    if (kpis.balance > 50) {
      out.push({ tipo: 'balance', prioridad: 'media',
        msg: `Spend sin facturar $${kpis.balance.toFixed(2)} — Meta va a cobrar la tarjeta pronto. Chequear que la tarjeta de Roberto tenga fondos.` })
    }
    return out
  }, [kpis, data, sugerencias])

  async function aplicarSugerencia(sug) {
    if (!confirm(`Aplicar sugerencia: "${sug.titulo}"?\n\n${sug.descripcion}\n\n(Fase 1: solo se marca como aplicada, la acción real en Meta se implementa en Fase 2)`)) return
    await supabase.from('meta_ads_sugerencias')
      .update({ estado: 'aplicada', aplicada_at: new Date().toISOString() })
      .eq('id', sug.id)
    setSugerencias(sugerencias.filter(s => s.id !== sug.id))
    alert('Marcada como aplicada. La ejecución de acciones (pausar/subir budget) llega en Fase 2.')
  }

  async function descartarSugerencia(sug) {
    const motivo = prompt('¿Por qué la descartás?', '') || ''
    await supabase.from('meta_ads_sugerencias')
      .update({ estado: 'descartada', descartada_at: new Date().toISOString(), descartada_motivo: motivo })
      .eq('id', sug.id)
    setSugerencias(sugerencias.filter(s => s.id !== sug.id))
  }

  // --------------------------------------------------------------------------
  // Render
  // --------------------------------------------------------------------------
  return (
    <AdminLayout title="Meta Ads">
      <div className="mads-wrap">
        <header className="mads-head">
          <div>
            <div className="mads-eyebrow">MARKETING · META ADS</div>
            <h1 className="mads-title">Meta Ads · {data?.account?.name || 'Limpack Publicidad'}</h1>
            <div className="mads-sub">
              {data?.account?.currency || 'USD'} · {data?.account?.timezone_name || 'America/Montevideo'}
              {ultimaSync && <> · sincronizado {ultimaSync.toLocaleTimeString('es-UY', { hour: '2-digit', minute: '2-digit' })}</>}
            </div>
          </div>
          <div className="mads-head-actions">
            <button onClick={() => cargar({ recalcular: true })} className="mads-btn mads-btn-ghost" disabled={loading}>
              {loading ? 'Sincronizando…' : '↻ Actualizar'}
            </button>
            <a href="https://business.facebook.com/adsmanager" target="_blank" rel="noopener" className="mads-btn mads-btn-ghost">
              Meta Ads Manager ↗
            </a>
          </div>
        </header>

        {error && <div className="mads-err">Error: {error}</div>}

        {loading && !data && (
          <div className="mads-loading">Cargando datos de Meta Ads…</div>
        )}

        {data && kpis && (<>
          {/* Alertas */}
          {alertas.map((a, i) => (
            <div key={i} className={`mads-alerta mads-alerta-${a.prioridad}`}>⚠️ {a.msg}</div>
          ))}

          {/* KPIs — 8 cards */}
          <div className="mads-kpis">
            <div className="mads-kpi">
              <div className="mads-kpi-label">GASTADO HOY</div>
              <div className="mads-kpi-num">${kpis.spendHoy.toFixed(2)}</div>
              <div className="mads-kpi-sub">clicks {kpis.clicksHoy} · reach {kpis.impresionesHoy.toLocaleString()}</div>
            </div>
            <div className="mads-kpi">
              <div className="mads-kpi-label">GASTADO MES</div>
              <div className="mads-kpi-num">${kpis.spendMes.toFixed(2)}</div>
              <div className="mads-kpi-sub">promedio ${(kpis.spendMes / Math.max(1, new Date().getDate())).toFixed(2)}/día</div>
            </div>
            <div className="mads-kpi">
              <div className="mads-kpi-label">CPC HOY</div>
              <div className="mads-kpi-num" style={{ color: kpis.cpcProm > 0.20 ? '#B91C1C' : kpis.cpcProm > 0 ? '#059669' : '#94A3B8' }}>
                ${kpis.cpcProm.toFixed(3)}
              </div>
              <div className="mads-kpi-sub">bench UY $0.10-0.20</div>
            </div>
            <div className="mads-kpi">
              <div className="mads-kpi-label">CTR HOY</div>
              <div className="mads-kpi-num" style={{ color: kpis.ctrProm > 1.5 ? '#059669' : '#94A3B8' }}>
                {kpis.ctrProm.toFixed(2)}%
              </div>
              <div className="mads-kpi-sub">bench IG frío 0.5-1%</div>
            </div>
            <div className="mads-kpi">
              <div className="mads-kpi-label">LANDING VIEWS HOY</div>
              <div className="mads-kpi-num">{kpis.lpvHoy}</div>
              <div className="mads-kpi-sub">{kpis.clicksHoy > 0 ? `${(kpis.lpvHoy / kpis.clicksHoy * 100).toFixed(0)}% de clicks` : '—'}</div>
            </div>
            <div className={`mads-kpi ${kpis.signupsMes === 0 && kpis.spendMes > 3 ? 'mads-kpi-warn' : ''}`}>
              <div className="mads-kpi-label">SIGNUPS DEL MES</div>
              <div className="mads-kpi-num">{kpis.signupsMes}</div>
              <div className="mads-kpi-sub">atribuidos por tiempo</div>
            </div>
            <div className="mads-kpi">
              <div className="mads-kpi-label">CPA REAL MES</div>
              <div className="mads-kpi-num">{kpis.cpaMes ? `$${kpis.cpaMes.toFixed(2)}` : '—'}</div>
              <div className="mads-kpi-sub">spend / signup</div>
            </div>
            <div className="mads-kpi" title="Lo que Meta te va a cobrar en la próxima factura. Se resetea al llegar al threshold (empieza en $25).">
              <div className="mads-kpi-label">SIN FACTURAR</div>
              <div className="mads-kpi-num">${kpis.balance.toFixed(2)}</div>
              <div className="mads-kpi-sub">gasto total histórico ${kpis.amountSpentTotal.toFixed(2)}</div>
            </div>
          </div>

          {/* Sugerencias del agente */}
          {sugerencias.length > 0 && (
            <div className="mads-sugs">
              <div className="mads-sugs-head">
                <span className="mads-sugs-title">💡 Sugerencias del agente</span>
                <span className="mads-sugs-count">{sugerencias.length}</span>
              </div>
              {sugerencias.map(s => (
                <div key={s.id} className={`mads-sug mads-sug-${s.prioridad}`}>
                  <div className="mads-sug-body">
                    <div className="mads-sug-titulo">{s.titulo}</div>
                    <div className="mads-sug-desc">{s.descripcion}</div>
                  </div>
                  <div className="mads-sug-actions">
                    <button onClick={() => aplicarSugerencia(s)} className="mads-btn mads-btn-primary">Aplicar</button>
                    <button onClick={() => descartarSugerencia(s)} className="mads-btn mads-btn-ghost">Descartar</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Tabla de campañas */}
          <div className="mads-camps">
            <div className="mads-camps-head">
              CAMPAÑAS · {data.campaigns?.length || 0}
            </div>
            {(!data.campaigns || data.campaigns.length === 0) && (
              <div className="mads-empty">No hay campañas activas. Creá una en Meta Ads Manager.</div>
            )}
            {(data.campaigns || []).map(c => {
              const ins = (data.insights || []).find(i => i.campaign_id === c.id)
              const insHoy = ins?.hoy
              const insTotal = ins?.total
              const actionsTotal = insTotal?.actions || []
              const lpvTotal = actionsTotal.find(a => a.action_type === 'landing_page_view')?.value || '0'
              const expandida = campExpandidaId === c.id
              return (
                <div key={c.id} className="mads-camp">
                  <div className="mads-camp-row" onClick={() => setCampExpandidaId(expandida ? null : c.id)}>
                    <div className="mads-camp-nombre">
                      <span className={`mads-badge mads-badge-${c.effective_status?.toLowerCase() || 'unknown'}`}>
                        {c.effective_status}
                      </span>
                      <strong>{c.name}</strong>
                    </div>
                    <div className="mads-camp-mini">
                      <div><span>Budget</span><strong>${c.daily_budget ? (parseInt(c.daily_budget)/100).toFixed(0) : '—'}/día</strong></div>
                      <div><span>Spend hoy</span><strong>${parseFloat(insHoy?.spend || '0').toFixed(2)}</strong></div>
                      <div><span>Spend total</span><strong>${parseFloat(insTotal?.spend || '0').toFixed(2)}</strong></div>
                      <div><span>Clicks</span><strong>{insTotal?.clicks || 0}</strong></div>
                      <div><span>Landing</span><strong>{lpvTotal}</strong></div>
                      <div><span>CPC</span><strong>${parseFloat(insTotal?.cpc || '0').toFixed(3)}</strong></div>
                    </div>
                    <div className="mads-camp-caret">{expandida ? '▲' : '▼'}</div>
                  </div>
                  {expandida && (
                    <div className="mads-camp-detalle">
                      <div className="mads-camp-detalle-grid">
                        <div><span>Objetivo</span><strong>{c.objective}</strong></div>
                        <div><span>Inicio</span><strong>{c.start_time?.slice(0,10)}</strong></div>
                        <div><span>Fin</span><strong>{c.stop_time?.slice(0,10) || '—'}</strong></div>
                        <div><span>Impresiones</span><strong>{insTotal?.impressions || 0}</strong></div>
                        <div><span>Alcance</span><strong>{insTotal?.reach || 0}</strong></div>
                        <div><span>CTR</span><strong>{parseFloat(insTotal?.ctr || '0').toFixed(2)}%</strong></div>
                        <div><span>Frecuencia</span><strong>{parseFloat(insTotal?.frequency || '0').toFixed(2)}</strong></div>
                        <div><span>Post engagement</span><strong>{actionsTotal.find(a=>a.action_type==='post_engagement')?.value || 0}</strong></div>
                      </div>
                      {/* Histórico diario desde snapshots */}
                      <div className="mads-hist-title">Últimos días</div>
                      <table className="mads-hist">
                        <thead><tr><th>Fecha</th><th>Spend</th><th>Clicks</th><th>LPV</th><th>Signups</th><th>CPA</th></tr></thead>
                        <tbody>
                          {snapshots.filter(s => s.campaign_id === c.id).slice(0, 14).map(s => (
                            <tr key={s.id}>
                              <td>{s.fecha}</td>
                              <td>${parseFloat(s.spend_usd || 0).toFixed(2)}</td>
                              <td>{s.clicks}</td>
                              <td>{s.landing_page_views}</td>
                              <td>{s.signups_atribuidos}</td>
                              <td>{s.cpa_real_usd ? `$${parseFloat(s.cpa_real_usd).toFixed(2)}` : '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <div className="mads-camp-actions">
                        <span className="mads-camp-actions-hint">Acciones (pausar / duplicar / cambiar budget) llegan en Fase 2.</span>
                        <a href={`https://business.facebook.com/adsmanager/manage/campaigns?act=${data.account?.id?.replace('act_','')}&selected_campaign_ids=${c.id}`} target="_blank" rel="noopener" className="mads-btn mads-btn-ghost">
                          Ver en Ads Manager ↗
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </>)}

        <div className="mads-footer">
          <Link to="/admin/marketing" className="mads-btn mads-btn-ghost">← Volver a Marketing Hub</Link>
        </div>
      </div>

      <style>{styles}</style>
    </AdminLayout>
  )
}

const styles = `
.mads-wrap { padding: 16px; max-width: 1400px; margin: 0 auto; font-family: Inter, system-ui, sans-serif; color: #1a3461; }
.mads-head { display: flex; justify-content: space-between; align-items: flex-end; gap: 16px; margin-bottom: 20px; flex-wrap: wrap; }
.mads-eyebrow { font-size: 11px; font-weight: 800; color: #64748b; letter-spacing: 0.08em; }
.mads-title { font-size: 26px; font-weight: 900; margin: 4px 0 6px; letter-spacing: -0.01em; }
.mads-sub { font-size: 12px; color: #64748b; }
.mads-head-actions { display: flex; gap: 8px; }
.mads-btn { padding: 8px 14px; border-radius: 8px; font-size: 13px; font-weight: 600; cursor: pointer; border: 0; font-family: inherit; text-decoration: none; display: inline-block; transition: filter .15s; }
.mads-btn-ghost { background: white; border: 1px solid #E2E8F0; color: #1a3461; }
.mads-btn-ghost:hover { background: #F8FAFC; }
.mads-btn-primary { background: linear-gradient(135deg,#1a5cb8,#00A8B5); color: white; }
.mads-btn-primary:hover { filter: brightness(1.1); }
.mads-btn:disabled { opacity: .6; cursor: wait; }
.mads-err { padding: 14px; background: #FEF2F2; border: 1px solid #FCA5A5; color: #B91C1C; border-radius: 10px; margin-bottom: 16px; font-size: 13px; }
.mads-loading { padding: 40px; text-align: center; color: #64748b; }
.mads-alerta { padding: 12px 16px; border-radius: 10px; margin-bottom: 12px; font-size: 13px; font-weight: 600; }
.mads-alerta-alta { background: #FEF3C7; border: 1px solid #FCD34D; color: #78350F; }
.mads-alerta-critica { background: #FEE2E2; border: 1px solid #FCA5A5; color: #991B1B; }
.mads-kpis { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-bottom: 16px; }
@media (max-width: 900px) { .mads-kpis { grid-template-columns: repeat(2, 1fr); } }
.mads-kpi { background: white; padding: 12px 14px; border-radius: 12px; border: 1px solid #E2E8F0; }
.mads-kpi-warn { border-color: #F59E0B; background: #FFFBEB; }
.mads-kpi-label { font-size: 10px; font-weight: 700; color: #64748b; letter-spacing: 0.05em; }
.mads-kpi-num { font-size: 22px; font-weight: 900; margin-top: 4px; letter-spacing: -0.01em; }
.mads-kpi-sub { font-size: 11px; color: #94a3b8; margin-top: 2px; }
.mads-sugs { background: white; border: 1px solid #E2E8F0; border-radius: 12px; padding: 14px 16px; margin-bottom: 16px; }
.mads-sugs-head { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; }
.mads-sugs-title { font-size: 14px; font-weight: 800; }
.mads-sugs-count { background: #1a5cb8; color: white; font-size: 11px; font-weight: 700; padding: 2px 8px; border-radius: 999px; }
.mads-sug { display: flex; justify-content: space-between; gap: 14px; padding: 12px 14px; border-radius: 10px; margin-bottom: 8px; align-items: flex-start; }
.mads-sug-alta { background: #FEF3C7; border: 1px solid #FCD34D; }
.mads-sug-media { background: #F8FAFC; border: 1px solid #E2E8F0; }
.mads-sug-baja { background: #F8FAFC; border: 1px solid #E2E8F0; }
.mads-sug-critica { background: #FEE2E2; border: 1px solid #FCA5A5; }
.mads-sug-body { flex: 1; }
.mads-sug-titulo { font-size: 13px; font-weight: 700; margin-bottom: 4px; color: #1a3461; }
.mads-sug-desc { font-size: 12px; color: #64748b; line-height: 1.55; }
.mads-sug-actions { display: flex; gap: 6px; flex-shrink: 0; }
.mads-camps { background: white; border: 1px solid #E2E8F0; border-radius: 12px; padding: 14px 16px; }
.mads-camps-head { font-size: 12px; font-weight: 800; color: #64748b; letter-spacing: 0.06em; margin-bottom: 12px; }
.mads-empty { padding: 20px; text-align: center; color: #94a3b8; font-size: 13px; }
.mads-camp { border: 1px solid #E2E8F0; border-radius: 10px; margin-bottom: 10px; overflow: hidden; }
.mads-camp-row { display: grid; grid-template-columns: 2fr 3fr 24px; gap: 12px; padding: 12px 14px; align-items: center; cursor: pointer; }
.mads-camp-row:hover { background: #F8FAFC; }
.mads-camp-nombre { display: flex; align-items: center; gap: 10px; font-size: 13px; flex-wrap: wrap; }
.mads-badge { font-size: 9px; font-weight: 800; letter-spacing: 0.05em; padding: 3px 8px; border-radius: 999px; }
.mads-badge-active { background: #DCFCE7; color: #166534; }
.mads-badge-paused { background: #FEF3C7; color: #78350F; }
.mads-badge-unknown { background: #F1F5F9; color: #64748B; }
.mads-camp-mini { display: grid; grid-template-columns: repeat(6, 1fr); gap: 8px; font-size: 11px; }
.mads-camp-mini > div { display: flex; flex-direction: column; gap: 1px; }
.mads-camp-mini span { color: #94a3b8; font-size: 10px; }
.mads-camp-mini strong { font-size: 13px; color: #1a3461; }
.mads-camp-caret { color: #94a3b8; font-size: 12px; }
.mads-camp-detalle { padding: 14px 16px; background: #F8FAFC; border-top: 1px solid #E2E8F0; }
.mads-camp-detalle-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-bottom: 14px; font-size: 12px; }
.mads-camp-detalle-grid > div { display: flex; flex-direction: column; gap: 2px; }
.mads-camp-detalle-grid span { color: #94a3b8; font-size: 10px; font-weight: 700; letter-spacing: 0.04em; }
.mads-camp-detalle-grid strong { font-size: 13px; color: #1a3461; }
.mads-hist-title { font-size: 11px; font-weight: 800; color: #64748b; letter-spacing: 0.06em; margin: 8px 0; }
.mads-hist { width: 100%; border-collapse: collapse; font-size: 12px; }
.mads-hist th, .mads-hist td { text-align: left; padding: 6px 8px; border-bottom: 1px solid #E2E8F0; }
.mads-hist th { font-weight: 700; color: #64748b; font-size: 10px; letter-spacing: 0.04em; }
.mads-camp-actions { display: flex; justify-content: space-between; align-items: center; margin-top: 12px; padding-top: 10px; border-top: 1px dashed #E2E8F0; }
.mads-camp-actions-hint { font-size: 11px; color: #94a3b8; font-style: italic; }
.mads-footer { margin-top: 24px; padding-top: 16px; border-top: 1px solid #E2E8F0; }
`
